import {
  canRead,
  canWrite,
  hasAnyAdmin,
  NO_PERMISSIONS,
  type Area,
  type Level,
  type MyPermissions,
} from "@/lib/permissions";
import { setPermissionsSnapshot } from "@/lib/permissions-snapshot";
import { queryKeys } from "@/lib/queryKeys";
import { permissionsProvider } from "@/providers/permissions.provider";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";

// The endpoint reads a short cache and can lag enforcement by up to a minute,
// so this is only ever used to decide what to render — never as the final word.
// Every admin call still has to survive a 403.
export function useMyPermissionsQuery() {
  const query = useQuery({
    queryKey: queryKeys.myPermissions(),
    queryFn: () => permissionsProvider.getMine(),
    staleTime: 60_000,
    retry: false,
  });

  // Mirror into a module the transport layer can read; React Query remains the
  // source of truth.
  useEffect(() => {
    setPermissionsSnapshot(query.data ?? null);
  }, [query.data]);

  return query;
}

export interface PermissionsView {
  me: MyPermissions;
  isLoading: boolean;
  /** True when the call itself was denied — the account is suspended. */
  isDenied: boolean;
  levelOf: (area: Area) => Level;
  canRead: (area: Area) => boolean;
  canWrite: (area: Area) => boolean;
  hasAnyAdmin: boolean;
}

export function usePermissions(): PermissionsView {
  const { data, isLoading, isError } = useMyPermissionsQuery();
  const me = data ?? NO_PERMISSIONS;

  return {
    me,
    isLoading,
    isDenied: isError,
    levelOf: useCallback((area: Area) => me.permissions[area], [me]),
    canRead: useCallback((area: Area) => canRead(me, area), [me]),
    canWrite: useCallback((area: Area) => canWrite(me, area), [me]),
    hasAnyAdmin: hasAnyAdmin(me),
  };
}

export function useRefreshPermissions() {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: queryKeys.myPermissions() }),
    [queryClient],
  );
}
